import { GroupByMode, PageEvents } from '@griply/types';
import { useSortedGoals, useUpdateGoal } from 'features/goals';
import { useUpdateLifeArea } from 'features/life-areas';
import {
  useGoalsOverviewFilterDocument,
  useUpdateUserSettings,
} from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GoalTimelineContainer } from 'shared/components/ui/timeline';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Goal as AppGoal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { TimelineTimeframes } from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import { useGoalsOverviewOrder } from '../../hooks/use-goals-overview-order';

export type TimelineProps = {
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  goals: AppGoal[];
  lifeAreas: LifeArea[];
  weekStartsOn: WeekDays;
};

export const Timeline: React.FC<TimelineProps> = ({
  timeframe,
  referenceDate,
  weekStartsOn,
  lifeAreas,
  goals,
}) => {
  useTrackPageVisitEvent(PageEvents.GoalsTimeline, undefined);
  const { t } = useTranslation();
  const user = useUser();
  const allGoals = useSortedGoals();
  const filterDocument = useGoalsOverviewFilterDocument();
  const customOrder = useGoalsOverviewOrder();

  const openCreateGoalDialog = useOpenCreateGoalDialog();
  const updateUserSettings = useUpdateUserSettings();
  const { submit: updateGoal } = useUpdateGoal();
  const { submit: updateLifeArea } = useUpdateLifeArea();

  const _createGoalForLifeArea = (id: ID) =>
    openCreateGoalDialog({ lifeAreaId: id === lifeAreaOtherID ? null : id });

  const _updateGoal = (
    id: ID,
    {
      startDate,
      deadline,
      goalSorting,
      parentIds,
    }: {
      startDate?: Timestamp;
      deadline?: Timestamp;
      goalSorting?: ID[];
      parentIds?: ID[] | null;
    },
  ) => {
    const goalToUpdate: UpdateGoal = { id };
    if (startDate || startDate === null) {
      goalToUpdate.startDate = startDate;
    }
    if (deadline || deadline === null) {
      goalToUpdate.deadline = deadline;
    }
    if (goalSorting) {
      goalToUpdate.goalSorting = goalSorting;
    }
    if (parentIds?.length || parentIds === null) {
      goalToUpdate.parentIds = parentIds;
    }

    updateGoal(goalToUpdate);
  };

  const _updateLifeArea = (id: ID, { goalSorting }: { goalSorting: ID[] }) =>
    updateLifeArea({ id, goalSorting });

  const _updateCollapsedLifeAreas = (ids: ID[]) =>
    updateUserSettings({
      goalsOverview: { roadmap: { collapsedItems: ids } },
    });

  return (
    <GoalTimelineContainer
      timeframe={timeframe}
      referenceDate={referenceDate}
      goals={goals}
      allGoals={allGoals}
      lifeAreas={lifeAreas}
      weekStartsOn={weekStartsOn}
      groupBy={filterDocument.groupByMode ?? GroupByMode.None}
      customOrder={customOrder}
      onChangeGoal={_updateGoal}
      onChangeLifeArea={_updateLifeArea}
      onChangeCollapsedItems={_updateCollapsedLifeAreas}
      collapsedItems={user?.settings?.goalsOverview?.roadmap?.collapsedItems}
      onCategoryAdd={_createGoalForLifeArea}
      title={t('pages.goals.roadmap.timeline.title')}
    />
  );
};
